import { apiFetch } from './client'
import { cached, invalidatePrefix } from './cache'

export interface CashClosing {
  id: string
  closing_number: number
  date_from: string
  date_to: string
  responsible: string
  opened_by?: string
  opened_at?: string
  status: 'draft' | 'received' | 'voided'
  opening_cash: number
  total_cash: number
  total_card: number
  total_transfer: number
  total_other: number
  grand_total: number
  total_expenses: number
  payment_count: number
  counted_cash?: number
  received_by?: string
  notes?: string
  received_at?: string
  voided_at?: string
  void_reason?: string
  created_at: string
  updated_at: string
}

export interface CashClosingExpense {
  id: string
  cash_closing_id: string
  description: string
  amount: number
  created_at: string
}

export interface CashClosingSnapshotPayment {
  id: string
  paidAt: string
  amount: number
  paymentMethodId: string | null
  status: string
  displayName: string | null
  typeName: string | null
}

export interface CashClosingSnapshot {
  capturedAt: string
  payments: CashClosingSnapshotPayment[]
  expenses: { description: string; amount: number }[]
}

export interface CreateCashClosingInput {
  openedBy: string
  dateFrom?: string
  dateTo?: string
  responsible?: string
  openingCash?: number
  notes?: string
}

export interface UpdateCashClosingInput {
  dateFrom?: string
  dateTo?: string
  responsible?: string
  openingCash?: number
  notes?: string
  openedAt?: string
}

export interface ReceiveCashClosingInput {
  receivedBy: string
  countedCash?: number
}

export const getCashClosings = () =>
  cached('cash-closings', () => apiFetch<{ closings: CashClosing[] }>('/cash-closings'), 120_000)

function notifyCashClosingsChanged() {
  window.dispatchEvent(new CustomEvent('cash-closings-changed'))
}

export const createCashClosing = async (data: CreateCashClosingInput) => {
  const r = await apiFetch<{ closing: CashClosing }>('/cash-closings', { method: 'POST', body: JSON.stringify(data) })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const updateCashClosing = async (id: string, data: UpdateCashClosingInput) => {
  const r = await apiFetch<{ closing: CashClosing }>(`/cash-closings/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const receiveCashClosing = async (id: string, data: ReceiveCashClosingInput) => {
  const r = await apiFetch<{ closing: CashClosing }>(`/cash-closings/${id}/receive`, { method: 'POST', body: JSON.stringify(data) })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const voidCashClosing = async (id: string, reason?: string) => {
  const r = await apiFetch<{ closing: CashClosing }>(`/cash-closings/${id}/void`, { method: 'POST', body: JSON.stringify({ reason }) })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const deleteCashClosing = async (id: string) => {
  const r = await apiFetch<{ ok: boolean }>(`/cash-closings/${id}`, { method: 'DELETE' })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const recalculateCashClosing = async (id: string) => {
  const r = await apiFetch<{ closing: CashClosing }>(`/cash-closings/${id}/recalculate`, { method: 'POST' })
  invalidatePrefix('cash-closings')
  notifyCashClosingsChanged()
  return r
}

export const getCashClosingSnapshot = (id: string) =>
  apiFetch<{ snapshot: CashClosingSnapshot }>(`/cash-closings/${id}/snapshot`)

export const getCashClosingExpenses = (id: string) =>
  apiFetch<{ expenses: CashClosingExpense[] }>(`/cash-closings/${id}/expenses`)

export const addCashClosingExpense = (id: string, data: { description: string; amount: number }) =>
  apiFetch<{ expense: CashClosingExpense }>(`/cash-closings/${id}/expenses`, { method: 'POST', body: JSON.stringify(data) })

export const deleteCashClosingExpense = (id: string, expenseId: string) =>
  apiFetch<{ ok: boolean }>(`/cash-closings/${id}/expenses/${expenseId}`, { method: 'DELETE' })
